/**
 * EnderecoFields — formulário de UM endereço titulado (ADR-0071).
 *
 * Ordem dos campos: CEP, Título, Logradouro, Número, Complemento, Bairro,
 * Cidade e UF. O CEP vem primeiro porque ele preenche o resto: ao completar
 * 8 dígitos (digitando ou colando) o componente chama `buscar(cep)` e grava
 * logradouro/bairro/cidade/UF — só com o que a busca devolveu preenchido,
 * para não apagar o que a pessoa já tinha escrito — e leva o foco ao Número,
 * que a busca nunca sabe. O título (obrigatório, nomeia o cartão) vem logo
 * depois do CEP.
 *
 * A grade acompanha a largura de onde o endereço está, não a da janela: o
 * mesmo componente vive no painel de 640px do cliente e na coluna estreita do
 * cockpit do lead, onde a grade fixa de 2 colunas cortava CEP, complemento e
 * UF (ver `GradeDoEndereco`).
 *
 * Props:
 *   - value: endereço ({titulo, cep, logradouro, numero, complemento, bairro,
 *     cidade, estado}); campos ausentes contam como vazios.
 *   - onChange(endereco): recebe o endereço inteiro a cada alteração.
 *   - buscar?(cep8digitos): Promise<endereco | null>. null = CEP não
 *     encontrado; rejeição = busca indisponível. Use `buscarCep` (interno,
 *     com o `request`) ou `buscarCepPublico` (página /f/slug), de
 *     `services/enderecos`. Sem `buscar`, o CEP é só um campo.
 *   - disabled?: desabilita todos os campos.
 *   - erros?: `{campo: string | string[]}` — o formato de um item de
 *     `enderecos` no 400 da API ou de `validarEndereco`. `item` = erro do
 *     endereço inteiro, anunciado (role=alert) e ligado ao CEP, ao
 *     logradouro e à cidade, que são os campos que o resolvem.
 *   - idPrefix: prefixo dos ids (`${idPrefix}-cep`, `${idPrefix}-numero`...).
 *     Precisa ser único na página e estável entre renders.
 *
 * O CEP recebe a máscara 00000-000 no blur, só na tela: o pai recebe o texto
 * digitado e o backend normaliza. Sair do campo não gera `onChange`, então
 * um CEP vindo do servidor não aparece como alteração pendente.
 */
import React, { useEffect, useRef, useState } from "react";

import { Field, Input, Select } from "../../pages/Orcamentos/styles";
import {
  AVISOS_DA_BUSCA_DE_CEP,
  CAMPOS_MINIMOS,
  ERRO_DO_ITEM,
  ESTADO_BUSCA_CEP,
  LIMITES,
  UFS,
  enderecoVazio,
  mascaraCep,
  normalizarCep,
  normalizarUf,
} from "../../utils/endereco";
import { AvisoDoCampo, GradeDoEndereco } from "./styles";

const ENDERECO_VAZIO = enderecoVazio();

const {
  OCIOSA: BUSCA_OCIOSA,
  BUSCANDO,
  NAO_ENCONTRADO: CEP_NAO_ENCONTRADO,
  INDISPONIVEL: BUSCA_INDISPONIVEL,
} = ESTADO_BUSCA_CEP;

/** O que a busca de CEP sabe. Número e complemento só a pessoa sabe. */
const CAMPOS_DA_BUSCA = ["logradouro", "bairro", "cidade", "estado"];

function mensagemDe(erros, campo) {
  const erro = erros?.[campo];
  if (!erro) return "";
  return Array.isArray(erro) ? erro.join(" ") : String(erro);
}

function mesmoCep(a, b) {
  const cepA = normalizarCep(a);
  const cepB = normalizarCep(b);
  if (cepA && cepB) return cepA === cepB;
  return String(a ?? "") === String(b ?? "");
}

function preenchidosPelaBusca(resultado) {
  return CAMPOS_DA_BUSCA.reduce((campos, campo) => {
    const valor = String(resultado?.[campo] ?? "").trim();
    if (!valor) return campos;
    return { ...campos, [campo]: campo === "estado" ? normalizarUf(valor) || valor : valor };
  }, {});
}

function CampoEndereco({ id, rotulo, erro, aviso, className, children }) {
  return (
    <Field as="div" className={className}>
      <label htmlFor={id}>{rotulo}</label>
      {children}
      {aviso}
      {erro && (
        <AvisoDoCampo id={`${id}-erro`} $tom="danger">
          {erro}
        </AvisoDoCampo>
      )}
    </Field>
  );
}

export default function EnderecoFields({
  value,
  onChange,
  buscar,
  disabled = false,
  erros,
  idPrefix = "endereco",
}) {
  const endereco = value || ENDERECO_VAZIO;

  // A busca responde depois de um `await`: nesse meio-tempo a pessoa pode ter
  // digitado outro campo (ou o pai trocado o `onChange`). As refs garantem que
  // a resposta mescla no endereço ATUAL, não no que existia quando a busca
  // começou.
  const atualRef = useRef(endereco);
  atualRef.current = endereco;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const cepBuscadoRef = useRef("");
  const montadoRef = useRef(false);
  const numeroRef = useRef(null);

  const [textoCep, setTextoCep] = useState(() => mascaraCep(endereco.cep));
  const [busca, setBusca] = useState(BUSCA_OCIOSA);
  const [focarNumero, setFocarNumero] = useState(false);

  useEffect(() => {
    montadoRef.current = true;
    return () => {
      montadoRef.current = false;
    };
  }, []);

  // O CEP mudou por fora (carregou o registro, reset do form): reflete, sem
  // pisar no que a pessoa está digitando quando é o mesmo CEP.
  useEffect(() => {
    setTextoCep((atual) => (mesmoCep(atual, endereco.cep) ? atual : mascaraCep(endereco.cep)));
  }, [endereco.cep]);

  // Foco depois do render que já trouxe os campos preenchidos.
  useEffect(() => {
    if (!focarNumero) return;
    setFocarNumero(false);
    numeroRef.current?.focus();
  }, [focarNumero]);

  const emitir = (novo) => {
    atualRef.current = novo;
    if (onChangeRef.current) onChangeRef.current(novo);
  };
  const alterarCampo = (campo, valor) => emitir({ ...atualRef.current, [campo]: valor });

  const respostaAindaVale = (cep) =>
    montadoRef.current
    && cepBuscadoRef.current === cep
    && normalizarCep(atualRef.current.cep) === cep;

  const buscarEndereco = async (cep) => {
    cepBuscadoRef.current = cep;
    setBusca(BUSCANDO);
    let resultado;
    try {
      resultado = await buscar(cep);
    } catch {
      // Toda falha que não é "não encontrado" tem o mesmo desfecho para quem
      // está preenchendo: seguir à mão. O aviso diz que o problema é a busca.
      if (respostaAindaVale(cep)) setBusca(BUSCA_INDISPONIVEL);
      return;
    }
    if (!respostaAindaVale(cep)) return;
    if (!resultado) {
      setBusca(CEP_NAO_ENCONTRADO);
      return;
    }
    setBusca(BUSCA_OCIOSA);
    emitir({ ...atualRef.current, ...preenchidosPelaBusca(resultado) });
    setFocarNumero(true);
  };

  const aoDigitarCep = (evento) => {
    const texto = evento.target.value;
    setTextoCep(texto);
    alterarCampo("cep", texto);
    const cep = normalizarCep(texto);
    if (!cep) {
      cepBuscadoRef.current = "";
      setBusca(BUSCA_OCIOSA);
      return;
    }
    if (buscar && cep !== cepBuscadoRef.current) buscarEndereco(cep);
  };

  const aoSairDoCep = () => setTextoCep((atual) => mascaraCep(atual));

  const idDe = (campo) => `${idPrefix}-${campo}`;
  const idErroDoItem = `${idPrefix}-${ERRO_DO_ITEM}-erro`;
  const erroDoItem = mensagemDe(erros, ERRO_DO_ITEM);

  const propsDoCampo = (campo, descricaoExtra) => {
    const erro = mensagemDe(erros, campo);
    const descritoPor = [
      descricaoExtra,
      erro && `${idDe(campo)}-erro`,
      // "Informe ao menos o CEP, o logradouro ou a cidade" é resolvido nestes
      // campos: o leitor de tela precisa ouvir o motivo neles.
      erroDoItem && CAMPOS_MINIMOS.includes(campo) && idErroDoItem,
    ]
      .filter(Boolean)
      .join(" ");
    return {
      id: idDe(campo),
      disabled,
      "aria-invalid": erro ? true : undefined,
      "aria-describedby": descritoPor || undefined,
    };
  };

  const propsDeTexto = (campo) => ({
    ...propsDoCampo(campo),
    type: "text",
    value: endereco[campo] ?? "",
    maxLength: LIMITES[campo],
    onChange: (evento) => alterarCampo(campo, evento.target.value),
  });

  const ufNormalizada = normalizarUf(endereco.estado);
  // UF gravada antes da validação ("XX"): continua visível e selecionada até
  // a pessoa escolher outra, em vez de sumir do select sem aviso.
  const ufForaDaLista = ufNormalizada === null;
  const valorUf = ufForaDaLista ? endereco.estado : ufNormalizada;

  const idAvisoBusca = `${idDe("cep")}-busca`;
  const avisoBusca = AVISOS_DA_BUSCA_DE_CEP[busca];

  return (
    <GradeDoEndereco>
      <CampoEndereco
        id={idDe("cep")}
        rotulo="CEP"
        erro={mensagemDe(erros, "cep")}
        aviso={
          <AvisoDoCampo id={idAvisoBusca} role="status" $tom={avisoBusca?.tom}>
            {avisoBusca?.texto || ""}
          </AvisoDoCampo>
        }
      >
        <Input
          {...propsDoCampo("cep", idAvisoBusca)}
          type="text"
          inputMode="numeric"
          value={textoCep}
          maxLength={LIMITES.cep}
          onChange={aoDigitarCep}
          onBlur={aoSairDoCep}
          placeholder="00000-000"
        />
      </CampoEndereco>

      <CampoEndereco id={idDe("titulo")} rotulo="Título *" erro={mensagemDe(erros, "titulo")}>
        <Input {...propsDeTexto("titulo")} aria-required="true" placeholder="Ex.: Casa, Obra" />
      </CampoEndereco>

      <CampoEndereco
        id={idDe("logradouro")}
        rotulo="Logradouro"
        erro={mensagemDe(erros, "logradouro")}
        className="full"
      >
        <Input {...propsDeTexto("logradouro")} placeholder="Rua, avenida..." />
      </CampoEndereco>

      <CampoEndereco id={idDe("numero")} rotulo="Número" erro={mensagemDe(erros, "numero")}>
        <Input {...propsDeTexto("numero")} ref={numeroRef} />
      </CampoEndereco>

      <CampoEndereco id={idDe("complemento")} rotulo="Complemento" erro={mensagemDe(erros, "complemento")}>
        <Input {...propsDeTexto("complemento")} placeholder="Apto, bloco, sala..." />
      </CampoEndereco>

      <CampoEndereco
        id={idDe("bairro")}
        rotulo="Bairro"
        erro={mensagemDe(erros, "bairro")}
        className="full"
      >
        <Input {...propsDeTexto("bairro")} />
      </CampoEndereco>

      <CampoEndereco id={idDe("cidade")} rotulo="Cidade" erro={mensagemDe(erros, "cidade")}>
        <Input {...propsDeTexto("cidade")} />
      </CampoEndereco>

      <CampoEndereco id={idDe("estado")} rotulo="UF" erro={mensagemDe(erros, "estado")}>
        <Select
          {...propsDoCampo("estado")}
          value={valorUf}
          onChange={(evento) => alterarCampo("estado", evento.target.value)}
        >
          <option value="">Selecione</option>
          {ufForaDaLista && <option value={endereco.estado}>{`${endereco.estado} (inválida)`}</option>}
          {UFS.map((uf) => (
            <option key={uf.sigla} value={uf.sigla}>
              {`${uf.sigla} — ${uf.nome}`}
            </option>
          ))}
        </Select>
      </CampoEndereco>

      {erroDoItem && (
        <AvisoDoCampo id={idErroDoItem} className="full" role="alert" $tom="danger">
          {erroDoItem}
        </AvisoDoCampo>
      )}
    </GradeDoEndereco>
  );
}
